<script lang="ts">
  import ProblemNotice from '../lib/ProblemNotice.svelte'
  import { appProblem } from '../lib/problems'
  import { t } from '../lib/i18n.svelte'
  import { dev } from '../lib/dev.svelte'
  import { account } from '../lib/account.svelte'
  import { friends } from '../lib/friends.svelte'
  import { live } from '../lib/live.svelte'
  import { navigate, viewerPath } from '../lib/router.svelte'
  import { parseRoomTarget } from '../lib/room-target'
  import { defaultSignalUrl } from '../lib/signaling'
  import { watchSharedRoom } from '../lib/room-navigation'
  import { canHost, isMobileBuild } from '../lib/platform'
  import FriendsRail from '../lib/FriendsRail.svelte'
  import Brand from '../lib/Brand.svelte'
  import DevLogin from '../lib/DevLogin.svelte'
  import Loading from '../lib/Loading.svelte'
  import RoomPreview from '../lib/RoomPreview.svelte'
  let previewFriend = $state<string | null>(null)

  let code = $state('')
  let error = $state<string | null>(null)

  const signedIn = $derived(account.signedIn)
  const online = $derived(friends.friends.filter((f) => f.presence.state !== 'offline'))
  const sharing = $derived(online.filter((f) => f.presence.state === 'sharing'))

  let linkCopied = $state(false)
  async function copyLiveLink() {
    if (!live.room) return
    try {
      await navigator.clipboard.writeText(live.room.shareUrl)
      linkCopied = true
      setTimeout(() => (linkCopied = false), 1500)
    } catch {
      // The link is on the host page either way.
    }
  }

  function join(event: SubmitEvent) {
    event.preventDefault()
    try {
      const target = parseRoomTarget(code, defaultSignalUrl(), !isMobileBuild)
      error = null
      navigate(viewerPath(target.code, target))
    } catch (caught) {
      error = caught instanceof Error ? caught.message : String(caught)
    }
  }
  const roomOf = (p: (typeof friends.friends)[number]['presence']) =>
    p.state === 'sharing' ? p : null
  const initial = (name: string) => name.slice(0, 1).toUpperCase()
</script>

{#snippet joinRow()}
  <form class="join-row" onsubmit={join}>
    <span>{t('Got a link?')}</span>
    <input
      bind:value={code}
      placeholder={t('Paste a link or room code')}
      aria-label={t('Room code')}
      autocomplete="off"
      spellcheck="false"
    />
    <button type="submit" class="join-row__go" disabled={code.trim() === ''}>{t('Join')}</button>
  </form>
  {#if error}<ProblemNotice problem={appProblem(error, 'link')} compact />{/if}
{/snippet}

{#if account.restoring}
  <Loading />
{:else if signedIn && canHost}
  <!-- SIGNED IN: friends sidebar + hero + live now -->
  <div class="home-split">
    <FriendsRail />

    <main class="home-main">
      {#if live.room}
        <section class="hero-card hero-card--live">
          <div class="hero-card__thumb placeholder">
            <span class="hero-card__tag">{live.room.title}</span>
          </div>
          <div class="hero-card__text">
            <h1>{t("You're live.")}</h1>
            <p>
              {live.room.viewers > 0
                ? t('{n} on the sofa. Invite more from the list, or copy your link.', {
                    n: live.room.viewers,
                  })
                : t('Nobody is on the sofa yet. Invite friends from the list, or copy your link.')}
            </p>
            <div class="row" style="gap: 10px">
              <button type="button" class="primary btn-lg" onclick={() => navigate('/host')}>
                {t('Back to stream')}
              </button>
              <button type="button" class="btn-outline btn-lg" onclick={copyLiveLink}>
                {linkCopied ? t('Copied') : t('Copy link')}
              </button>
            </div>
          </div>
        </section>
      {:else}
        <section class="hero-card">
          <div class="hero-card__text">
            <h1>{t('Put something on the sofa.')}</h1>
            <p>{t('One window and its sound, straight to friends. They just open a link.')}</p>
            <div class="row" style="gap: 16px">
              <button type="button" class="primary btn-xl" onclick={() => navigate('/host')}>
                {t('Share a window')}
              </button>
            </div>
          </div>
          <div class="hero-card__thumb placeholder">{t('last shared window')}</div>
        </section>
      {/if}

      <section class="stack" style="gap: 14px">
        <div class="spread">
          <h2 class="section-title">{t('Live now')}</h2>
          <span class="faint"
            >{live.room
              ? t('{n} other friends sharing', { n: sharing.length })
              : t('{n} friends sharing', { n: sharing.length })}</span
          >
        </div>
        {#if sharing.length}
          <div class="live-cards">
            {#each sharing as f (f.user_id)}
              {#if f.presence.state === 'sharing'}
                <button
                  type="button"
                  class="live-card"
                  onmouseenter={() => (previewFriend = f.user_id)}
                  onmouseleave={() => (previewFriend = null)}
                  onfocus={() => (previewFriend = f.user_id)}
                  onblur={() => (previewFriend = null)}
                  onclick={() => {
                    const room = roomOf(f.presence)
                    if (room) watchSharedRoom(room, f.user_id)
                  }}
                >
                  <span class="live-card__thumb">
                    <RoomPreview room={f.presence} active={previewFriend === f.user_id} />
                    <span class="live-tag"><span class="dot"></span>LIVE</span>
                    <span class="live-card__watch">{t('Watch')}</span>
                  </span>
                  <span class="live-card__meta">
                    <span class="avatar avatar--sm">{initial(f.name)}</span>
                    <strong>{f.name}</strong>
                  </span>
                </button>
              {/if}
            {/each}
          </div>
        {:else}
          <p class="muted">{t('When a friend starts sharing, it shows up here.')}</p>
        {/if}
      </section>

      <div style="margin-top: auto">{@render joinRow()}</div>
    </main>
  </div>
{:else}
  <!-- SIGNED OUT (or browser / mobile): sign-in is the goal, sharing stays free -->
  <div class="home-out">
    <header class="home-out__bar">
      <Brand size={26} />
      <span class="row" style="gap: 20px">
        <a href="/settings">{t('Settings')}</a>
        {#if account.available}
          <button type="button" class="btn-dark" onclick={() => account.signInWith('google')}>
            {t('Sign in')}
          </button>
        {/if}
      </span>
    </header>

    <div class="home-out__body">
      <div class="home-out__hero">
        <div>
          <h1 class="display-xl">
            {isMobileBuild ? t('Join a room') : t('Share one window with a few friends.')}
          </h1>
          {#if !isMobileBuild}
            <p class="lede">
              {t(
                'Send a link. They open it in any browser. One window, its sound, 1080p at 60 fps. Peer to peer and encrypted.',
              )}
            </p>
            <div class="row" style="gap: 16px; margin-top: 28px">
              {#if canHost}
                <button type="button" class="btn-outline btn-lg" onclick={() => navigate('/host')}>
                  {t('Share a window')}
                </button>
                <span class="faint">{t('Works without an account')}</span>
              {:else}
                <a href="/host"
                  ><button type="button" class="btn-outline btn-lg"
                    >{t('Open the host panel')}</button
                  ></a
                >
              {/if}
            </div>
          {/if}
        </div>

        {#if account.available && !isMobileBuild}
          <aside class="signin-card">
            <div>
              <h2>{t('Better with friends.')}</h2>
              <p>{t('Sign in once and your sofa follows you.')}</p>
            </div>
            <ul>
              <li>{t('See who is online and sharing')}</li>
              <li>{t('Invite friends in one click')}</li>
              <li>{t('Keep your friends on a new PC')}</li>
            </ul>
            {#if account.error}<ProblemNotice
                problem={appProblem(account.error, 'account')}
                compact
              />{/if}
            <DevLogin />
            <div class="stack" style="gap: 10px">
              <button
                type="button"
                class="primary btn-lg"
                disabled={account.busy}
                onclick={() => account.signInWith('google')}
              >
                {t('Continue with Google')}
              </button>
            </div>
          </aside>
        {/if}
      </div>

      {@render joinRow()}
      {#if dev.on}<a href="/debug">{t('Protocol debug')}</a>{/if}
    </div>
  </div>
{/if}
